import { type InfiniteData, useMutation, useQueryClient } from "react-query";
import { api } from "utils/api";
import { openCallKeys, submissionKeys } from "./queryKeys";
import type {
  OpenCallSubmission,
  OpenCallSubmissionPostBody,
  OpenCallSubmissionsResponse,
} from "../types";

const updateSubmission = (params: OpenCallSubmissionPostBody) =>
  api.put<OpenCallSubmission>(
    `/open-call-submission/${params.openCallId}/${params.submissionId}`,
    {
      body: JSON.stringify(params.openCallSubmission),
    }
  );

const useUpdateSubmission = () => {
  const queryClient = useQueryClient();

  return useMutation(updateSubmission, {
    onSuccess: (updated) => {
      // Invalidate submission's OpenCall data
      queryClient.invalidateQueries(openCallKeys.detail(updated.id));
      // Invalidate user's submission list
      queryClient.invalidateQueries(
        submissionKeys.user(updated.openCallId, updated.createdByUserId)
      );
      // Manually update submission in any OpenCall list
      queryClient.setQueriesData<
        OpenCallSubmissionsResponse | InfiniteData<OpenCallSubmissionsResponse>
      >({ queryKey: submissionKeys.lists() }, (oldData) => {
        if (!oldData) return { data: [] };
        const replaceUpdated = (submission: OpenCallSubmission) =>
          submission.id === updated.id &&
          submission.createdByUserId === updated.createdByUserId
            ? updated
            : submission;

        return "pages" in oldData
          ? {
              ...oldData,
              pages: oldData.pages.map((page) => ({
                ...page,
                data: page.data.map(replaceUpdated),
              })),
            }
          : {
              ...oldData,
              data: oldData.data.map(replaceUpdated),
            };
      });
    },
  });
};

export { useUpdateSubmission };
